import { useCreateTemplateMutation } from '@/services/contractTemplates';
import { Button, Modal, toast } from '@palmetto/palmetto-components';
import { Form, Formik } from 'formik';
import { useMemo } from 'react';

import { ContractDocumentProviders } from 'types';

import { isErrorWithData } from '../../services/helpers';

import { ContractTemplateFormFields } from './ContractTemplateFormFields';
import {
  contractTemplateSchema,
  ContractTemplateFormValues,
  toContractTemplatePayload,
} from './contractTemplateSchema';

export const AddContractModal = ({
  isOpen,
  programType,
  handleClose,
}: {
  isOpen: boolean;
  programType: string;
  handleClose: () => void;
}) => {
  const [createTemplate] = useCreateTemplateMutation();

  const handleSubmit = async (values: ContractTemplateFormValues) => {
    try {
      await createTemplate(toContractTemplatePayload(values)).unwrap();
      toast.success('Contract created');
      handleClose();
    } catch (e) {
      if (isErrorWithData(e)) {
        toast.error(e.data.message);
      } else {
        console.error(e);
        toast.error('Error creating contract template settings');
      }
    }
  };

  const initialValues: ContractTemplateFormValues = useMemo(
    () => ({
      programType,
      state: '',
      lseId: '',
      contractType: undefined,
      provider: ContractDocumentProviders.docusign,
      requiredAdditionalSigners: [],
      congaSpanishAvailable: false,
      vaultOnCompletion: '',
      eOriginalLender: '',
      englishTemplateId: '',
      englishCosignTemplateId: '',
      spanishTemplateId: '',
      spanishCosignTemplateId: '',
    }),
    [programType],
  );

  if (!isOpen) {
    return null;
  }

  return (
    <Formik
      key={programType}
      validationSchema={contractTemplateSchema}
      initialValues={initialValues}
      validateOnChange={false}
      onSubmit={handleSubmit}
      enableReinitialize={true}
    >
      {({ isSubmitting, isValidating, submitForm, errors }) => (
        <Form noValidate>
          <Modal isOpen={true} maxWidth="4xl" ariaLabelledBy="addContractHeader" onDismiss={handleClose}>
            <Modal.Header id="addContractHeader" title="Add Contract" onDismiss={handleClose} />
            <Modal.Body background="secondary" childGap="lg">
              <ContractTemplateFormFields />
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="secondary"
                tone="neutral"
                isLoading={isSubmitting}
                isDisabled={isSubmitting}
                onClick={handleClose}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                isLoading={isSubmitting}
                isDisabled={isSubmitting || isValidating || Object.keys(errors).length > 0}
                type="submit"
                onClick={submitForm}
              >
                Add Contract
              </Button>
            </Modal.Footer>
          </Modal>
        </Form>
      )}
    </Formik>
  );
};
